import { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle2, Loader2, ShieldCheck, ShoppingBag } from 'lucide-react';
import { useShopProduct } from '../../hooks/useShopify';
import { formatShopifyMoney, getShopProductCategory, shopifyConfigError } from '../../lib/shopify';
import { useShopStore } from '../../store/shopStore';

interface ShopProductScreenProps {
  productHandle: string | null;
  onNavigate: (screen: any, params?: any) => void;
}

export default function ShopProductScreen({ productHandle, onNavigate }: ShopProductScreenProps) {
  const { product, error, isLoading, isConfigured } = useShopProduct(productHandle);
  const addItem = useShopStore((state) => state.addItem);
  const markRecentlyViewed = useShopStore((state) => state.markRecentlyViewed);
  const cartCount = useShopStore((state) => state.lines.reduce((sum, line) => sum + line.quantity, 0));

  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setAdded(false);
    setQuantity(1);
    if (!product) {
      setSelectedVariantId(null);
      return;
    }

    const initialVariant = product.variants.find((variant) => variant.availableForSale) ?? product.variants[0] ?? null;
    setSelectedVariantId(initialVariant?.id ?? null);
    setSelectedImageUrl(initialVariant?.image?.url ?? product.featuredImage?.url ?? product.images[0]?.url ?? null);
  }, [product]);

  const selectedVariant = product?.variants.find((variant) => variant.id === selectedVariantId)
    ?? product?.variants[0]
    ?? null;
  const galleryImages = product
    ? [selectedVariant?.image ?? null, product.featuredImage, ...product.images]
        .filter((image): image is NonNullable<typeof product.featuredImage> => Boolean(image))
        .filter((image, index, images) => images.findIndex((candidate) => candidate.url === image.url) === index)
    : [];
  const heroImage = galleryImages.find((image) => image.url === selectedImageUrl)
    ?? galleryImages[0]
    ?? null;
  const selectionSubtotal = selectedVariant
    ? formatShopifyMoney({
        amount: String(Number(selectedVariant.price.amount) * quantity),
        currencyCode: selectedVariant.price.currencyCode,
      })
    : '';

  const addToCart = () => {
    if (!product || !selectedVariant) return;
    addItem({
      productId: product.id,
      productHandle: product.handle,
      merchandiseId: selectedVariant.id,
      productTitle: product.title,
      variantTitle: selectedVariant.title,
      imageUrl: (selectedVariant.image ?? product.featuredImage ?? product.images[0] ?? null)?.url ?? null,
      imageAlt: (selectedVariant.image ?? product.featuredImage ?? product.images[0] ?? null)?.altText ?? null,
      priceAmount: Number(selectedVariant.price.amount),
      currencyCode: selectedVariant.price.currencyCode,
      quantity,
    });
    setAdded(true);
  };

  useEffect(() => {
    if (!product || !selectedVariant) {
      return;
    }

    markRecentlyViewed({
      productId: product.id,
      productHandle: product.handle,
      productTitle: product.title,
      imageUrl: (selectedVariant.image ?? product.featuredImage ?? product.images[0] ?? null)?.url ?? null,
      imageAlt: (selectedVariant.image ?? product.featuredImage ?? product.images[0] ?? null)?.altText ?? null,
      priceAmount: Number(selectedVariant.price.amount),
      currencyCode: selectedVariant.price.currencyCode,
      category: getShopProductCategory(product),
    });
  }, [markRecentlyViewed, product, selectedVariant]);

  if (!isConfigured) {
    return (
      <StateScreen
        title="Shop not configured"
        body={shopifyConfigError ?? 'Shopify env vars are missing.'}
        onBack={() => onNavigate('shop')}
      />
    );
  }

  if (!productHandle) {
    return (
      <StateScreen
        title="Product not found"
        body="Open the shop again and choose a product."
        onBack={() => onNavigate('shop')}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-background flex items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        Loading product…
      </div>
    );
  }

  if (error || !product) {
    return (
      <StateScreen
        title="Could not load product"
        body={error?.message ?? 'This product is unavailable.'}
        onBack={() => onNavigate('shop')}
      />
    );
  }

  return (
    <div className="w-full min-h-screen bg-background pb-28">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => onNavigate('shop')}
          className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg truncate">{product.title}</h1>
          <p className="text-xs text-muted-foreground">Shop product</p>
        </div>
        <button
          onClick={() => onNavigate('shop-cart')}
          className="relative w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
          aria-label="Open cart"
        >
          <ShoppingBag className="w-5 h-5" />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#1A6B4F] text-white text-[10px] flex items-center justify-center">
              {cartCount}
            </span>
          )}
        </button>
      </div>

      <div className="px-6 py-6 space-y-5">
        <div className="overflow-hidden rounded-[2rem] border border-border bg-card">
          <div className="relative h-80 bg-secondary sm:h-[26rem]">
            {heroImage ? (
              <img src={heroImage.url} alt={heroImage.altText ?? product.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                No image
              </div>
            )}
            <div className="absolute left-4 top-4 flex gap-2">
              <span className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.16em] ${
                selectedVariant?.availableForSale ? 'bg-[#D4F04A]/85 text-[#14563F]' : 'bg-white/80 text-muted-foreground'
              }`}>
                {selectedVariant?.availableForSale ? 'In stock' : 'Unavailable'}
              </span>
              <span className="rounded-full bg-black/45 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-white">
                Shopify checkout
              </span>
            </div>
          </div>
          <div className="space-y-4 p-5">
            {galleryImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                {galleryImages.map((image) => {
                  const isActive = heroImage?.url === image.url;
                  return (
                    <button
                      key={image.url}
                      onClick={() => setSelectedImageUrl(image.url)}
                      className={`h-16 w-16 shrink-0 overflow-hidden rounded-2xl border ${
                        isActive ? 'border-[#1A6B4F]' : 'border-border'
                      }`}
                    >
                      <img src={image.url} alt={image.altText ?? product.title} className="h-full w-full object-cover" />
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-2xl leading-tight">{product.title}</div>
                <div className="mt-2 text-sm text-muted-foreground">
                  {product.variants.length} variant{product.variants.length === 1 ? '' : 's'} available in this product.
                </div>
              </div>
              <div className="rounded-2xl bg-[#D4F04A]/15 px-3 py-2 text-right">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#1A6B4F]">From</div>
                <div className="mt-1 text-base font-medium text-[#1A6B4F]">
                  {formatShopifyMoney(product.priceRange.minVariantPrice)}
                </div>
              </div>
            </div>
            <div className="text-[#1A6B4F] font-medium">
              {formatShopifyMoney(selectedVariant?.price ?? product.priceRange.minVariantPrice)}
            </div>
            {product.description && (
              <p className="text-sm text-muted-foreground mt-3 whitespace-pre-line">{product.description}</p>
            )}
          </div>
        </div>

        {product.variants.length > 1 && (
          <div>
            <div className="text-sm font-medium mb-2">Choose a variant</div>
            <div className="grid gap-2">
              {product.variants.map((variant) => {
                const isActive = variant.id === selectedVariant?.id;
                return (
                  <button
                    key={variant.id}
                    onClick={() => setSelectedVariantId(variant.id)}
                    className={`p-3 rounded-2xl border text-left transition-colors ${
                      isActive
                        ? 'border-[#1A6B4F] bg-[#D4F04A]/10'
                        : 'border-border bg-card hover:border-[#1A6B4F]/40'
                    } ${!variant.availableForSale ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm">{variant.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {variant.availableForSale ? 'In stock' : 'Unavailable'}
                        </div>
                      </div>
                      <div className="text-sm text-[#1A6B4F]">{formatShopifyMoney(variant.price)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-card border border-border rounded-[2rem] p-4 space-y-4">
          <div className="rounded-2xl bg-secondary/60 p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <ShieldCheck className="w-4 h-4 text-[#1A6B4F]" />
              Secure hosted checkout
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Payment, shipping, taxes, and final order confirmation happen on Shopify after you leave Deuce.
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm">Quantity</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setQuantity((current) => Math.max(1, current - 1))}
                className="w-9 h-9 rounded-full border border-border hover:bg-secondary"
              >
                -
              </button>
              <div className="w-10 text-center text-sm">{quantity}</div>
              <button
                onClick={() => setQuantity((current) => Math.min(20, current + 1))}
                className="w-9 h-9 rounded-full border border-border hover:bg-secondary"
              >
                +
              </button>
            </div>
          </div>

          {added && (
            <div className="text-sm text-[#1A6B4F] flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Added to cart.
            </div>
          )}

          <div className="rounded-2xl border border-border bg-background px-4 py-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Selected variant</span>
              <span>{selectedVariant?.title === 'Default Title' ? 'Standard' : selectedVariant?.title}</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Estimated subtotal</span>
              <span className="font-medium text-[#1A6B4F]">{selectionSubtotal}</span>
            </div>
          </div>

          <button
            onClick={addToCart}
            disabled={!selectedVariant?.availableForSale}
            className="w-full h-12 rounded-2xl bg-[#1A6B4F] hover:bg-[#14563F] text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {selectedVariant?.availableForSale ? 'Add to cart' : 'Unavailable'}
          </button>
          <button
            onClick={() => onNavigate('shop-cart')}
            className="w-full h-12 rounded-2xl bg-secondary hover:bg-muted text-foreground"
          >
            View cart
          </button>
        </div>
      </div>
    </div>
  );
}

function StateScreen({
  title,
  body,
  onBack,
}: {
  title: string;
  body: string;
  onBack: () => void;
}) {
  return (
    <div className="w-full min-h-screen bg-background flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <div className="text-xl mb-2">{title}</div>
        <div className="text-sm text-muted-foreground mb-5">{body}</div>
        <button onClick={onBack} className="h-11 px-5 rounded-xl bg-[#1A6B4F] text-white text-sm">
          Back to shop
        </button>
      </div>
    </div>
  );
}